import type { MediaProgress } from '../../api/types';
import { liveBook, type BookSummary } from '../models';
import { filterBooks, normalizeFilterOptions, type BookFilterOptions } from './filters';
import { searchBooks } from './search';
import { sortBooks } from './sort';

/** Flutter's library page size. */
export const LIBRARY_PAGE_SIZE = 20;

export interface LibraryQueryInput {
  query?: string;
  options?: Partial<BookFilterOptions>;
  /** Live progress to join in before filtering (progressStore). */
  progress?: Record<string, MediaProgress>;
  /** Ids playable offline for this account. */
  downloaded?: Set<string>;
}

export interface LibraryQueryResult {
  /** Every match, sorted. */
  books: BookSummary[];
  filteredCount: number;
  totalCount: number;
}

/** Search → join live state → filter → sort, over cached rows (pure). */
export function queryLibrary(rows: readonly BookSummary[], input: LibraryQueryInput = {}): LibraryQueryResult {
  const options = normalizeFilterOptions(input.options);
  const matched = searchBooks(rows, input.query ?? '');
  const progress = input.progress ?? {};
  const downloaded = input.downloaded ?? new Set<string>();
  const joined = matched.map((b) => liveBook(b, progress, downloaded));
  const filtered = filterBooks(joined, options);
  const books = sortBooks(filtered, options.sortBy, options.sortOrder);
  return { books, filteredCount: books.length, totalCount: rows.length };
}

/** `[offset, offset + limit)`; negative offsets clamp to 0, `limit <= 0` returns the rest. */
export function pageBooks<T>(books: readonly T[], offset: number, limit = LIBRARY_PAGE_SIZE): T[] {
  const start = Math.max(0, offset);
  return limit <= 0 ? books.slice(start) : books.slice(start, start + limit);
}
